import React, { useEffect } from 'react';
import styled from 'App/styled';
import { DEFAULT_LANGUAGE } from '@nykaa/intl-shared/constants';
import { PageLocationType } from '@nykaa/product-card-shared/types';
import { VariantOptionTypes } from '@nykaa/wishlist-shared/types';

import { PrimaryCategories, Variant } from 'store/product/types';
import { CartData } from 'store/addToCart/types';
import Wishlist from './Wishlist';
import AddToCart from './AddToCart';
import { PageJourney } from 'store/dataLayer/action';
import { useSelector } from 'react-redux';
import { State } from 'store/types';

const Wrapper = styled.div<{ recoWidgetFullScreen: boolean }>`
  bottom: 0px;
  width: 100%;
  padding-left: 15px;
  padding-right: 15px;
  z-index: 1;
  max-width: 640px;
  margin: auto;

  &.initial {
    position: fixed;
    bottom: ${({ theme }) => theme.spacing.spacing0};
    width: 100%;
    ${({ recoWidgetFullScreen, theme }) =>
      recoWidgetFullScreen
        ? `padding-top: ${theme.spacing.spacing60}; padding-bottom: ${theme.spacing.spacing20};`
        : 'padding-top: 0; padding-bottom: 0;'};
  }

  &.action-v2 {
    padding: ${({ theme: { spacing } }) => spacing.spacing50};
    background: ${({ theme }) => theme.colors.surface};
    box-shadow: ${({ theme }) => theme.elevations.shadow100};
  }

  &.top-sticky {
    position: sticky;
    bottom: 5px;
    &.sticky {
      padding-top: 0;
      padding-bottom: 0;
      margin-top: 5px;
    }
  }
`;

const Main = styled.div`
  display: flex;
  align-items: center;
  background: #fff;
  border-radius: ${({ theme, actionV2 }) =>
    actionV2 ? theme.borders.radius30 : '5px'};
  border: ${({ actionV2, theme }: any) =>
    !actionV2 ? `1px solid ${theme.brandPrimary}` : 'none'};
  height: 50px;
  overflow: hidden;

  & > button {
    height: 50px;
    padding: 13px;
  }

  &.main_wishlist {
    justify-content: center;
  }
  .wishlist_button {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
  }
  .wishlist_button_text {
    ${({ theme }) => theme.typography.bodyLarge};
    color: ${(props) => props.theme.brandPrimary};
    padding: 0px 25px;
  }
`;

interface ActionsProps {
  brandName: string;
  product: Variant | VariantOptionTypes;
  parentId: string;
  enableAnimation?: boolean;
  handleAddToCartTracking: (cartData: CartData) => void;
  onSuccessAddToBag?: VoidFunction;
  primaryCategories: PrimaryCategories;
  pageType?: string;
  pageLocation: PageLocationType;
  lang?: string;
  clickedFrom?: string;
  isOnlyAddToCart?: boolean;
  videoId?: string;
  ctaText?: string;
  onErrorAddToBag: () => void;
  newButtonText?: string;
  newCtaText?: string;
  isGoToBag?: boolean;
  setNewCtaText: (prop: string) => void;
  setIsGoToBag: (prop: boolean) => void;
  journeyData: PageJourney;
  recoWidgetFullScreen?: boolean;
  handlePostAddToCart?: () => void;
  isConvSearch?: boolean;
  gptConversationId?: string;
  appTrackingKeys?: { [key: string]: any };
  readUrlParamsForTracking?: boolean;
}

const onScrollStickyInfo = () => {
  if (!document) {
    return;
  }
  const containerElement = document.querySelector('#stickElement');
  const topStickyElement = document.querySelector('#topSticky');
  if (containerElement && topStickyElement) {
    const offsetBottom = containerElement.getBoundingClientRect().bottom;
    const cartElementTop = topStickyElement.getBoundingClientRect().top;
    const cartElementHeight = topStickyElement.clientHeight;
    if (offsetBottom < cartElementTop + cartElementHeight) {
      topStickyElement.classList.add('top-sticky');
      topStickyElement.classList.add('sticky');
      topStickyElement.classList.remove('initial');
    } else {
      topStickyElement.classList.remove('sticky');
      topStickyElement.classList.remove('top-sticky');
      topStickyElement.classList.add('initial');
    }
  }
};

function Actions({
  brandName,
  product,
  parentId,
  enableAnimation = true,
  handleAddToCartTracking,
  onSuccessAddToBag,
  primaryCategories,
  pageType,
  pageLocation,
  lang = DEFAULT_LANGUAGE,
  clickedFrom,
  videoId,
  ctaText,
  isOnlyAddToCart = false,
  onErrorAddToBag,
  newButtonText = '',
  newCtaText,
  isGoToBag,
  setNewCtaText,
  setIsGoToBag,
  journeyData,
  recoWidgetFullScreen,
  handlePostAddToCart,
  isConvSearch,
  gptConversationId,
  appTrackingKeys,
  readUrlParamsForTracking = false,
}: ActionsProps) {
  const isActionCTARevamped = useSelector(
    (state: State) => state.remoteConfig.switches.isPdpActionV2Enabled
  );
  useEffect(() => {
    if (enableAnimation) {
      document.addEventListener('scroll', onScrollStickyInfo, {
        passive: true,
      });
    }
    return () => {
      if (enableAnimation) {
        document.removeEventListener('scroll', onScrollStickyInfo);
      }
    };
  }, [enableAnimation]);
  const { onlyWishlistButton } = product;
  const mainWishListClass = onlyWishlistButton
    ? 'custom_main_wishlist main_wishlist'
    : 'custom_main_wishlist';
  const customWishlistButton = onlyWishlistButton
    ? 'custom_wishlist_button wishlist_button'
    : 'custom_wishlist_button';
  return (
    <Wrapper
      className={`initial ${isActionCTARevamped ? 'action-v2' : ''}`}
      id="topSticky"
      recoWidgetFullScreen={recoWidgetFullScreen}
    >
      <Main
        className={mainWishListClass}
        actionV2={isActionCTARevamped}
      >
        {!isOnlyAddToCart && (
          <Wishlist
            brandName={brandName}
            product={product}
            onlyWishlistButton={!!onlyWishlistButton}
            customWishlistButton={customWishlistButton}
            customWishlistText="wishlist_button_text"
            primaryCategories={primaryCategories}
            pageLocation={pageLocation}
            videoId={videoId}
            pageType={pageType}
            isConvSearch={isConvSearch}
            gptConversationId={gptConversationId}
            appTrackingKeys={appTrackingKeys}
          />
        )}
        {!onlyWishlistButton && (
          <AddToCart
            brandName={brandName}
            parentId={parentId}
            product={product}
            handleAddToCartTracking={handleAddToCartTracking}
            onSuccessAddToBag={onSuccessAddToBag}
            pageType={pageType}
            lang={lang}
            clickedFrom={clickedFrom}
            ctaText={ctaText}
            newButtonText={newButtonText}
            onErrorAddToBag={onErrorAddToBag}
            isOnlyAddToCart={isOnlyAddToCart}
            isGoToBag={isGoToBag}
            newCtaText={newCtaText}
            setNewCtaText={setNewCtaText}
            setIsGoToBag={setIsGoToBag}
            journeyData={journeyData}
            openedFrom={pageType}
            handlePostAddToCart={handlePostAddToCart}
            isConvSearch={isConvSearch}
            appTrackingKeys={appTrackingKeys}
            readUrlParamsForTracking={readUrlParamsForTracking}
          />
        )}
      </Main>
    </Wrapper>
  );
}

export default Actions;
